/**
 * Wire-format helpers for wireless-sensor (xPulse / Nevata) on-demand commands.
 *
 * These mirror the conventions the mobile app uses when calling
 * commandService.sendOnDemandCommand(...) for the same MT message types, so
 * the portal's outgoing dataStrList payloads match what the mobile app sends.
 * Verify against real device/backend behavior when wiring up a new command —
 * see memory: feedback_dont_assert_bugs_from_static_analysis.
 */
import type { NASecurityObject, NANevataSensorObject, NAMTObject } from '@/api/models';
import type { NAMTMessageType } from '@/types/enums';
import { SIREN_SYNC_MAP, ALARM_TYPE_MAP, GEO_AUTO_ARM_MAP, resolveOnlineState } from './enum-resolvers';
import { isSensorType } from './sensor-map';

/**
 * Finds the most recent not-yet-received MT command of the given type, optionally
 * narrowed further by its dataStrList (e.g. matching a specific zoneNum or combined
 * address). Mirrors the mobile app's repeated pattern:
 *   _deviceInfoObj.DeviceMTList.OrderByDescending(mt => mt.mtmsn)
 *     .Where(mt => mt.messageType.Equals(TYPE) && mt.messageReceived.Equals(false) [&& ...])
 *     .FirstOrDefault();
 * Used to show a "Pending" status + optimistic value on a control while a command
 * is still in the device's MT queue, exactly as mobile does throughout its settings
 * pages. `mt.messageType` is numeric on the wire (no StringEnumConverter anywhere in
 * this backend), same caution as every other enum field in this codebase.
 */
export function findPendingMT(
  mtObjList: NAMTObject[] | undefined,
  messageType: NAMTMessageType,
  matchFn?: (dataStrList: string[]) => boolean,
): NAMTObject | null {
  const matches = (mtObjList ?? []).filter((mt) => {
    if (mt.messageReceived !== false) return false;
    const mtType = typeof mt.messageType === 'string' ? Number(mt.messageType) : mt.messageType;
    if (mtType !== (messageType as number)) return false;
    if (matchFn && !matchFn(mt.dataStrList ?? [])) return false;
    return true;
  });
  matches.sort((a, b) => (b.mtmsn ?? 0) - (a.mtmsn ?? 0));
  return matches[0] ?? null;
}

/** Combines a sensor's MSB/LSB source address into the single decimal-string
 * address the mobile app sends as dataStrList[0] for sensor-addressed commands. */
export function getCombinedAddress(msb: number, lsb: number): string {
  return ((BigInt(msb) << 32n) | BigInt(lsb >>> 0)).toString();
}

export interface OptionDef {
  value: number;
  label: string;
}

function mapToOptions(map: Record<number, string>): OptionDef[] {
  return Object.entries(map).map(([value, label]) => ({ value: Number(value), label }));
}

/** The 8 zone types xPulse actually offers (mirrors mobile's _alarmTypeMap). */
export const ZONE_TYPE_OPTIONS: OptionDef[] = [24, 25, 26, 27, 28, 29, 30, 31].map((value) => ({
  value,
  label: ALARM_TYPE_MAP[value].label,
}));

export const SIREN_SYNC_OPTIONS: OptionDef[] = mapToOptions(SIREN_SYNC_MAP);

/**
 * Coerces a raw API field (numeric in production, sometimes a legacy TS
 * string-enum name in mock fixtures) to the numeric ordinal used for both
 * <select value=...> binding and the outgoing command payload.
 */
export function toOrdinal(val: unknown, legacyStringMap: Record<string, number>): number | null {
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    if (val in legacyStringMap) return legacyStringMap[val];
    const parsed = Number(val);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return null;
}

export const LEGACY_ALARM_TYPE_STRING_MAP: Record<string, number> = {
  ALARM_TYPE_ZONE_SECURITY_V2: 24,
  ALARM_TYPE_ZONE_SMOKE_V2: 25,
  ALARM_TYPE_ZONE_CO_V2: 26,
  ALARM_TYPE_ZONE_WATER_V2: 27,
  ALARM_TYPE_ZONE_TEMPERATURE_V2: 28,
  ALARM_TYPE_ZONE_DISARM_V2: 29,
  ALARM_TYPE_ZONE_MISC_V2: 30,
  ALARM_TYPE_ZONE_MISC_SIREN_V2: 31,
};

export const LEGACY_SIREN_SYNC_STRING_MAP: Record<string, number> = {
  DISABLED: 0,
  ENABLED: 1,
  ALARM_ENABLED: 3,
  ALARM_ONLY: 3,
};

// MT_MESSAGE_SET_GEO_AUTO_ARM_DISARM_MODE (Geofence Sync's Auto Arm/Disarm Mode
// dropdown) — backend's formatMTCommandPayloadByte (NAMTObject.cs) does
// `Convert.ToByte(dataStrList[0])`, wrapped in a try/catch that silently
// swallows the exception and writes 0 on failure. The dropdown was previously
// sending the legacy string enum name (e.g. "GEOFENCE_AUTO_ARM_DISARM_ALL_MODES")
// directly — Convert.ToByte on that always throws, so every selection except
// "Disabled" silently wrote 0 (Disabled) to the device regardless of what the
// user picked. Must send the numeric ordinal as a decimal string, same as
// Siren Sync above.
export const GEO_AUTO_ARM_OPTIONS: OptionDef[] = mapToOptions(GEO_AUTO_ARM_MAP);

export const LEGACY_GEO_AUTO_ARM_STRING_MAP: Record<string, number> = {
  GEOFENCE_AUTO_ARM_DISARM_DISABLED: 0,
  GEOFENCE_AUTO_ARM_DISARM_ONBOARD_MODE: 1,
  GEOFENCE_AUTO_ARM_DISARM_AWAY_MODE: 2,
  GEOFENCE_AUTO_ARM_DISARM_ALL_MODES: 3,
};

// ── Zone Type Category (DeviceCategory) — per-zone REST write, not an MT command ──
// Only shown/sent when the zone's Zone Type is Security (24). Options and ordinals
// mirror XPulseSettingsPageViewModel.cs's _zoneCategoryMap exactly. Ordinals verified
// against the backend's `enum DeviceCategory` (Globals.cs) — this endpoint
// (devicePropertiesV2 / DevicePropertiesHandlerV2) deserializes deviceCategory as a
// plain numeric enum value (no StringEnumConverter), so send the ordinal, not a name.
export const ZONE_TYPE_CATEGORY_OPTIONS: OptionDef[] = [
  { value: 7, label: 'Intrusion' }, // DEVICE_CATEGORY_SECURITY
  { value: 8, label: 'Electronics' }, // DEVICE_CATEGORY_ELECTRONICS
  { value: 14, label: 'Engine Cowling' }, // DEVICE_CATEGORY_ENGINE_COWLING
];

export const LEGACY_DEVICE_CATEGORY_STRING_MAP: Record<string, number> = {
  UNKNOWN: 0,
  DEVICE_CATEGORY_LOCATION: 1,
  DEVICE_CATEGORY_GEOFENCE: 2,
  DEVICE_CATEGORY_AC_POWER: 3,
  DEVICE_CATEGORY_DC_POWER: 4,
  DEVICE_CATEGORY_NEVATA: 5,
  DEVICE_CATEGORY_FLOAT_SWITCH: 6,
  DEVICE_CATEGORY_SECURITY: 7,
  DEVICE_CATEGORY_ELECTRONICS: 8,
  DEVICE_CATEGORY_SMOKE_CO: 9,
  DEVICE_CATEGORY_TEMPERATURE: 10,
  DEVICE_CATEGORY_MISC_SIREN: 11,
  DEVICE_CATEGORY_MISC_NO_SIREN: 12,
  DEVICE_CATEGORY_SWITCH: 13,
  DEVICE_CATEGORY_ENGINE_COWLING: 14,
};

/** Zone Type ordinals for which mobile hides the Zone Configuration (NO/NC) picker. */
export const HIDE_INPUT_TYPE_FOR_ZONE_TYPES = [27, 32]; // Water, Geo Arm/Disarm (VTracker)

/** Zone Type ordinal for Security — the only type that shows Zone Type Category. */
export const SECURITY_ZONE_TYPE = 24;

// ── Device-level built-in Zone 1-4 config ──
// Ported verbatim from DeviceConfigPageViewModel.cs's `_deviceZoneConfigList`
// (set up in initView(), lines 402-447). These are the device's own 4 built-in
// zone slots (sourceAddressLSB === 1) — NOT any specific wireless sensor's zones,
// and NOT the same 8-option list xPulse zones use. Each zoneNum has its own fixed
// set of allowed types, and critically, whether Security shows a category picker
// varies by zoneNum: zones 1-2 offer Security but with NO category list (empty),
// while zones 3-4 offer Security ONLY, with the full category list. Getting this
// per-zoneNum table wrong silently breaks either the option list or the category
// picker's visibility — copy it exactly, don't generalize it.
export const DEVICE_ZONE_TYPE_MAP: Record<number, Record<number, OptionDef[]>> = {
  1: { 24: [], 32: [] }, // Security (no category), Geo Arm/Disarm (VTracker)
  2: { 24: [], 27: [] }, // Security (no category), Water
  3: { 24: ZONE_TYPE_CATEGORY_OPTIONS },
  4: { 24: ZONE_TYPE_CATEGORY_OPTIONS },
};

/** Zone Type option list for a device-level built-in zone (1-4). */
export function deviceZoneTypeOptions(zoneNum: number): OptionDef[] {
  const map = DEVICE_ZONE_TYPE_MAP[zoneNum] ?? {};
  return Object.keys(map).map((ord) => ({ value: Number(ord), label: ALARM_TYPE_MAP[Number(ord)].label }));
}

/** Zone Type Category options for a device-level built-in zone's current type — empty if none. */
export function deviceZoneCategoryOptions(zoneNum: number, alarmTypeOrdinal: number): OptionDef[] {
  return DEVICE_ZONE_TYPE_MAP[zoneNum]?.[alarmTypeOrdinal] ?? [];
}

// ── xPulse zone selection ──
// Ported verbatim from XPulseSettingsPageViewModel.cs's refreshDeviceZones(). Two
// details are easy to get wrong and both matter:
//  1. The displayed "Zone N" (1-4) is a pure positional counter, NOT the zone's real
//     `zoneNum` — a sensor's 4 zones can have any starting zoneNum (e.g. 5-8) depending
//     on how many zones earlier-paired sensors already occupy. The outgoing MT command
//     for a zone always uses its real `zoneNum` (e.g. "5"), never the display position.
//  2. Zones 2-4 are found by an absolute zoneNum lookup (`startZoneNum+1`, `+2`, `+3`)
//     against the FULL securitySensorlist, not by re-matching this sensor's address.
//     This is what excludes system zones (Tamper/Inactivity/Low Battery, alarmType
//     58-60) that share the sensor's address but sit outside the 4-zone numeric range —
//     they're simply never in range, so they never enter the loop.

export interface XPulseZoneSlot {
  /** Positional display number, always 1-4 — what mobile shows as "Zone {n}". */
  zoneCounter: number;
  /** The zone's real zoneNum — what's sent in MT command payloads. */
  zoneNum: number;
  /** The matching security zone record, or null if not yet reported for this zoneNum. */
  zone: NASecurityObject | null;
}

/**
 * Computes this xPulse sensor's 4 zone slots, or null if the sensor's zone data
 * hasn't arrived yet / fails mobile's own sanity gate (startZoneNum < 4) — mirroring
 * `refreshDeviceZones()`'s early return exactly (which on mobile blanks the ENTIRE
 * settings page, not just the zone list, when this gate fails).
 */
export function getXPulseZoneSlots(
  sensor: { sourceAddressMSB: number; sourceAddressLSB: number },
  securityZones: NASecurityObject[],
): XPulseZoneSlot[] | null {
  const firstZone = securityZones.find(
    (z) => z.sourceAddressMSB === sensor.sourceAddressMSB && z.sourceAddressLSB === sensor.sourceAddressLSB,
  );
  if (!firstZone) return null;

  const startZoneNum = firstZone.zoneNum;
  if (startZoneNum < 4) return null;

  const slots: XPulseZoneSlot[] = [];
  for (let i = 0; i < 4; i++) {
    const zoneNum = startZoneNum + i;
    const zone = securityZones.find((z) => z.zoneNum === zoneNum) ?? null;
    slots.push({ zoneCounter: i + 1, zoneNum, zone });
  }
  return slots;
}

// ── Nevata water-level cascade logic ──
// Ported verbatim from NevataSettingsPageViewModel.cs (pumpTurnOnLevelChangedCallback /
// highWaterLevelChangedCallback / criticalWaterLevelChangedCallback) so the three
// thresholds always maintain bilgeLevel < sirenLevel < criticalLevel.

export interface NevataLevels {
  bilgeLevel: number;
  sirenLevel: number;
  criticalLevel: number;
}

export function cascadeFromBilgeLevel(
  newBilgeLevel: number,
  current: NevataLevels,
  calibrationOffset: number,
): NevataLevels {
  const bilgeLevel = newBilgeLevel;
  let { sirenLevel, criticalLevel } = current;
  if (bilgeLevel >= sirenLevel) sirenLevel = bilgeLevel + 1;
  if (sirenLevel >= criticalLevel) {
    criticalLevel = sirenLevel + 1;
    if (criticalLevel >= calibrationOffset) criticalLevel = 0;
  }
  return { bilgeLevel, sirenLevel, criticalLevel };
}

export function cascadeFromSirenLevel(
  newSirenLevel: number,
  current: NevataLevels,
  calibrationOffset: number,
): NevataLevels {
  const sirenLevel = newSirenLevel;
  let { bilgeLevel, criticalLevel } = current;
  if (bilgeLevel >= sirenLevel) bilgeLevel = newSirenLevel - 1;
  if (sirenLevel >= criticalLevel) {
    criticalLevel = sirenLevel + 1;
    if (criticalLevel >= calibrationOffset) criticalLevel = 0;
  }
  return { bilgeLevel, sirenLevel, criticalLevel };
}

export function cascadeFromCriticalLevel(newCriticalLevel: number, current: NevataLevels): NevataLevels {
  const criticalLevel = newCriticalLevel;
  let { bilgeLevel, sirenLevel } = current;
  if (criticalLevel !== 0 && sirenLevel >= criticalLevel) sirenLevel = criticalLevel - 1;
  if (bilgeLevel >= sirenLevel) bilgeLevel = sirenLevel - 1;
  return { bilgeLevel, sirenLevel, criticalLevel };
}

/** Pump Turn On Level options: 1..(calibrationOffset-3) inclusive. */
export function pumpTurnOnLevelOptions(calibrationOffset: number): OptionDef[] {
  const opts: OptionDef[] = [];
  for (let i = 1; i <= calibrationOffset - 3; i++) opts.push({ value: i, label: `${i}"` });
  return opts;
}

/** High Water Level options: 2..(calibrationOffset-2) inclusive. */
export function highWaterLevelOptions(calibrationOffset: number): OptionDef[] {
  const opts: OptionDef[] = [];
  for (let i = 2; i <= calibrationOffset - 2; i++) opts.push({ value: i, label: `${i}"` });
  return opts;
}

/** Critical Water Level options: 0 (Disabled) plus 3..(calibrationOffset-2) inclusive. */
export function criticalWaterLevelOptions(calibrationOffset: number): OptionDef[] {
  const opts: OptionDef[] = [{ value: 0, label: 'Disabled' }];
  for (let i = 3; i <= calibrationOffset - 2; i++) opts.push({ value: i, label: `${i}"` });
  return opts;
}

// ── Wireless sensor connectivity ──
// NASensorMapObject (the unified identity record every wireless sensor is
// listed under) carries no onlineState of its own -- only the type-specific
// detail record does (NASecurityObject for xPulse zones, NANevataSensorObject
// for Nevata). Resolving "is this sensor offline" means joining back to
// whichever detail list matches the sensor's type and address.
export function wirelessSensorOnlineState(
  sensor: { sourceAddressMSB: number; sourceAddressLSB: number; sensorType: unknown },
  securityZones: NASecurityObject[],
  nevataSensorList: NANevataSensorObject[],
): string {
  if (isSensorType(sensor, 'LOOKOUT_SENSOR_TYPE')) {
    const zone = securityZones.find(
      (z) => z.sourceAddressMSB === sensor.sourceAddressMSB && z.sourceAddressLSB === sensor.sourceAddressLSB,
    );
    return resolveOnlineState(zone?.onlineState);
  }
  if (isSensorType(sensor, 'WATER_SENSOR_TYPE')) {
    const nev = nevataSensorList.find(
      (n) => n.sourceAddressMSB === sensor.sourceAddressMSB && n.sourceAddressLSB === sensor.sourceAddressLSB,
    );
    return resolveOnlineState(nev?.onlineState);
  }
  return 'Unknown';
}
